interface StringToColorOptions {
  excludeHueRange?: [number, number];
  lightness?: [number, number];
  saturation?: [number, number];
  seed?: number;
}

const DEFAULT_SATURATION_RANGE: [number, number] = [45, 65];
const DEFAULT_LIGHTNESS_RANGE: [number, number] = [45, 60];

/**
 * Ranges used by the Compass Avatar fallback. Keep in sync with
 * `@circleco/compass` Avatar internals so the sidebar gradient matches the
 * avatar fallback color exactly.
 */
const AVATAR_LIGHTNESS_RANGE: [number, number] = [25, 28];
const AVATAR_EXCLUDED_HUE_RANGE: [number, number] = [220, 245];

/** cyrb53 string hash — same implementation Compass uses for avatar colors. */
const hashString = (str: string, seed = 0): number => {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

const stringToColor = (
  str: string,
  options: StringToColorOptions = {},
): string => {
  const {
    seed = 0,
    saturation = DEFAULT_SATURATION_RANGE,
    lightness = DEFAULT_LIGHTNESS_RANGE,
    excludeHueRange,
  } = options;

  const hash = hashString(str, seed);
  let hue = hash % 360;

  if (excludeHueRange) {
    const [start, end] = excludeHueRange;
    const rangeSize = end - start;
    if (hue >= start && hue <= end) {
      hue = (end + 1 + (hue - start)) % 360;
    } else if (hue > end) {
      hue = ((hue - rangeSize - 1 + 360) % (360 - rangeSize - 1)) + end + 1;
      if (hue >= start && hue <= end) {
        hue = end + 1;
      }
    }
  }

  const s =
    saturation[0] +
    (((hash >>> 8) % 100) / 100) * (saturation[1] - saturation[0]);
  const l =
    lightness[0] +
    (((hash >>> 16) % 100) / 100) * (lightness[1] - lightness[0]);

  return `hsl(${hue}, ${Math.round(s)}%, ${Math.round(l)}%)`;
};

/** Same initials derivation as the Compass Avatar fallback. */
export const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0][0].toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/** The exact background color the Compass Avatar generates for a member. */
export const getAvatarFallbackColor = (name: string): string =>
  stringToColor(getInitials(name), {
    lightness: AVATAR_LIGHTNESS_RANGE,
    excludeHueRange: AVATAR_EXCLUDED_HUE_RANGE,
  });

const RGBA_REGEX = /rgba\((\d+),\s*(\d+),\s*(\d+)/;

export const computeGradientStyle = (rgba: string | undefined) => {
  if (!rgba) return {};
  const parts = RGBA_REGEX.exec(rgba);
  if (!parts) return {};
  const [, r, g, b] = parts;
  return {
    background: `linear-gradient(180deg, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0) 50%)`,
  };
};
